import { GENERATED_FILE_SHARE_MAX_TARGETS } from "../constants/generated-files";
import type { IGeneratedFileShareProject } from "../types/generated-files";
import type { IUserSearchItem } from "../types/user";
import type {
  IChunkShareUuidsProps,
  IFilterInvitableUsersProps,
  IFilterShareableProjectsProps,
  IFormatShareRemovalTitleProps,
  IGetFailedShareTargetsProps,
  IGetReachedShareTargetsProps,
  IGetSharedByNameProps,
} from "./generated-file-shares.types";
import { getUserHandle } from "./user";

// NOTE: NSAI-6501 — the platform-agnostic half of sharing one AI-generated
// file, read by the web dialog and the mobile sheet alike so the two cannot
// drift on who is offered, who is reported as failed, or how a removal is sent.

// NOTE: The server caps one removal at GENERATED_FILE_SHARE_MAX_TARGETS share
// uuids, while "Select all" on a long access list picks every row — so a
// removal is split into batches of at most that many, in their original order.
// A non-positive size is treated as the cap rather than looping forever.
export const chunkShareUuids = ({
  shareUuids,
  size = GENERATED_FILE_SHARE_MAX_TARGETS,
}: IChunkShareUuidsProps): string[][] => {
  const batchSize = size > 0 ? size : GENERATED_FILE_SHARE_MAX_TARGETS;
  const batches: string[][] = [];

  for (let start = 0; start < shareUuids.length; start += batchSize) {
    batches.push(shareUuids.slice(start, start + batchSize));
  }

  return batches;
};

// NOTE: A result names its target as it was sent: the email for a person, the
// uuid for a project — and, since NSAI-7736, the uuid for a person with no
// email. A uuid means nothing to the reader, so a project is named from the
// shareable-projects list and a person from the users the request named (the
// uuid stays as the last resort, when neither list knows it).
export const getFailedShareTargets = ({
  results,
  projects,
  users = [],
}: IGetFailedShareTargetsProps): string[] => {
  const projectNames = new Map(
    projects.map((project) => [project.uuid, project.name]),
  );
  const userNames = new Map(
    users.map((user) => [
      user.uuid,
      user.fullName?.trim() || getUserHandle(user) || user.uuid,
    ]),
  );

  return results
    .filter((result) => !result.success)
    .map((result) =>
      result.targetType === "project"
        ? (projectNames.get(result.target) ?? result.target)
        : (userNames.get(result.target) ?? result.target),
    );
};

// NOTE: The targets a share request did reach, lower-cased so a picked chip
// (an email as the user typed it, a project uuid) can be matched against them.
// Only these leave the invite field; a failed one stays picked for a retry.
export const getReachedShareTargets = ({
  results,
}: IGetReachedShareTargetsProps): ReadonlySet<string> =>
  new Set(
    results
      .filter((result) => result.success)
      .map((result) => result.target.toLowerCase()),
  );

// NOTE: Who shared a file with the caller: their name, else their email, else
// their IdP login name (NSAI-7736), else the caller-supplied fallback (a
// deactivated or nameless account).
export const getSharedByName = ({
  sharedBy,
  fallback,
}: IGetSharedByNameProps): string =>
  sharedBy?.name?.trim() ||
  sharedBy?.email?.trim() ||
  sharedBy?.username?.trim() ||
  fallback;

// NOTE: A project the file is already shared with has nothing left to offer, so
// it leaves the list; the rest are matched on name against the live query (the
// list is small and already in memory, so there is nothing to debounce).
export const filterShareableProjects = ({
  projects,
  shares,
  query,
}: IFilterShareableProjectsProps): IGeneratedFileShareProject[] => {
  const sharedProjectUuids = new Set(
    shares.flatMap((share) =>
      share.project !== null ? [share.project.uuid] : [],
    ),
  );
  const needle = query.trim().toLowerCase();

  return projects.filter(
    (project) =>
      !sharedProjectUuids.has(project.uuid) &&
      (needle.length === 0 || project.name.toLowerCase().includes(needle)),
  );
};

// NOTE: The owner cannot be invited to their own file, and a teammate who
// already has access has nothing to gain from a second invite. Both are matched
// by uuid or by (case-insensitive) email — and an email only when there is one:
// the search answers "" for a person with no address (NSAI-7736), and two of
// those are not the same person.
export const filterInvitableUsers = ({
  users,
  shares,
  currentUserUuid,
  currentUserEmail,
}: IFilterInvitableUsersProps): IUserSearchItem[] => {
  const sharedEmails = new Set(
    shares.flatMap((share) =>
      share.user?.email ? [share.user.email.toLowerCase()] : [],
    ),
  );
  const sharedUserUuids = new Set(
    shares.flatMap((share) => (share.user !== null ? [share.user.uuid] : [])),
  );
  const ownEmail = currentUserEmail?.toLowerCase() || null;

  return users.filter((user) => {
    const email = user.email.toLowerCase();

    return (
      user.uuid !== currentUserUuid &&
      !sharedUserUuids.has(user.uuid) &&
      (email === "" || (email !== ownEmail && !sharedEmails.has(email)))
    );
  });
};

// NOTE: NSAI-6653 — the title of the "remove access" confirmation. One row names
// who loses access (a person, or everyone in a project), so the owner confirms
// against the target they clicked rather than a bare count; a selection of
// several reads as a count of people or projects, where listing every name
// would not fit a dialog title.
export const formatShareRemovalTitle = ({
  shares,
  pendingIds,
  labels,
}: IFormatShareRemovalTitleProps): string => {
  if (pendingIds.length === 0) {
    return labels.generic;
  }

  if (pendingIds.length > 1) {
    return labels.formatSelected({ count: pendingIds.length });
  }

  const [pendingId] = pendingIds;
  const share = shares.find((candidate) => candidate.uuid === pendingId);

  if (share?.targetType === "project") {
    const projectName = share.project?.name.trim() ?? "";

    return projectName
      ? labels.formatProject({ name: projectName })
      : labels.generic;
  }

  const userName =
    share?.user?.name?.trim() ||
    share?.user?.email?.trim() ||
    share?.user?.username?.trim() ||
    "";

  return userName ? labels.formatUser({ name: userName }) : labels.generic;
};
